import { useMemo } from 'react';
import { useAuth, UserRole } from '@/contexts/AuthContext';

export const usePermissions = () => {
  const { userRole, userRoles, hasPermission, canAccess } = useAuth();

  // Multi-role aware flags using .includes()
  const roles = userRoles || [];

  return useMemo(() => {
    const isMaster = roles.includes('master');
    const isVendedorJunior = roles.includes('vendedor_junior');
    const isVendedorSenior = roles.includes('vendedor_senior');
    const isFinanceiroJunior = roles.includes('financeiro_junior');
    const isFinanceiroSenior = roles.includes('financeiro_senior');
    const isGestorComercial = roles.includes('gestor_comercial');
    const isComprador = roles.includes('comprador');
    const isEstoquista = roles.includes('estoquista');

    // Feature access checks
    const canAccessSettings = isMaster;
    const canViewDashboard = isMaster || canAccess('fin:view_reports');
    const canViewCustomers = canAccess('customers');
    const canViewEmployees = canAccess('employees');
    const canViewDeposits = canAccess('deposits');
    const canViewProducts = canAccess('products');
    const canViewRentals = canAccess('rentals');
    const canViewLoans = canAccess('logistics:loan_view');
    const canViewReports = canAccess('reports');
    const canViewSettings = canAccess('settings');

    // Action permissions
    const canCreate = canAccess('create');
    const canEdit = canAccess('edit');
    const canDelete = canAccess('delete');

    // PII access permissions - only masters can access sensitive customer data
    const canAccessCustomerPII = isMaster;

    // Menu visibility rules
    const getVisibleMenuItems = () => {
      const baseItems = [
        { id: 'dashboard', visible: canViewDashboard },
        { id: 'customers', visible: canViewCustomers },
        { id: 'employees', visible: canViewEmployees },
        { id: 'deposits', visible: canViewDeposits },
        { id: 'products', visible: canViewProducts },
        { id: 'loans', visible: canViewLoans },
      ];

      if (isMaster) {
        baseItems.push(
          { id: 'rentals', visible: canViewRentals },
          { id: 'loans', visible: canViewLoans },
          { id: 'reports', visible: canViewReports },
          { id: 'settings', visible: canViewSettings }
        );
      }

      return baseItems.filter(item => item.visible);
    };

    const shouldShowDeleteButton = (context?: string) => {
      return canDelete && isMaster;
    };

    const shouldShowEditButton = (context?: string) => {
      if (isMaster) return canEdit;
      if (context === 'customers') {
        return (
          canAccess('crm:access') ||
          canAccess('crm:approve_registrations') ||
          canAccess('crm:manage_portfolio') ||
          canAccess('customers')
        );
      }
      if (context) return canAccess(context);
      return false;
    };

    const shouldShowCreateButton = (context?: string) => {
      if (isMaster) return canCreate;
      if (context === 'customers') {
        return (
          canAccess('crm:access') ||
          canAccess('crm:approve_registrations') ||
          canAccess('crm:manage_portfolio') ||
          canAccess('customers')
        );
      }
      if (context === 'products') {
        return canAccess('products:create');
      }
      if (context) return canAccess(context);
      return false;
    };

    // 72h deletion window for sales/quotations
    const canDeleteSaleOrQuotation = (
      createdAt: string | Date,
      createdBy: string | null | undefined,
      currentUserId: string | null | undefined
    ): boolean => {
      if (isMaster) return true;
      if (!createdBy || !currentUserId || createdBy !== currentUserId) return false;
      
      const createdDate = new Date(createdAt);
      const now = new Date();
      const hoursSinceCreation = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
      
      return hoursSinceCreation <= 72;
    };

    const getRestrictedMessage = (requiredRole?: UserRole) => {
      if (!userRole) return "Você precisa fazer login para acessar esta funcionalidade.";
      
      if (requiredRole === 'master' && !isMaster) {
        return "Esta funcionalidade é restrita a usuários Master.";
      }

      return "Você não tem permissão para acessar esta funcionalidade.";
    };

    return {
      userRole,
      userRoles: roles,
      isMaster,
      isVendedorJunior,
      isVendedorSenior,
      isFinanceiroJunior,
      isFinanceiroSenior,
      isGestorComercial,
      isComprador,
      isEstoquista,
      hasPermission,
      canAccess,
      
      // Feature access
      canAccessSettings,
      canViewDashboard,
      canViewCustomers,
      canViewEmployees,
      canViewDeposits,
      canViewProducts,
      canViewRentals,
      canViewLoans,
      canViewReports,
      canViewSettings,
      
      // Actions
      canCreate,
      canEdit,
      canDelete,
      canAccessCustomerPII,
      
      // UI helpers
      getVisibleMenuItems,
      shouldShowDeleteButton,
      shouldShowEditButton,
      shouldShowCreateButton,
      getRestrictedMessage,
      canDeleteSaleOrQuotation,
    };
  }, [userRole, roles, hasPermission, canAccess]);
};
